export const VIDEO_UPSCALE_PATH = '/video-upscale';

export const VIDEO_UPSCALE_SEO_TITLE =
  '#1 AI Video Upscaler to 4K | Enhance Video Online';

export const VIDEO_UPSCALE_SEO_DESCRIPTION =
  'Use the AI video upscaler for MP4 or MOV clips. Upscale video to 4K when the input is large enough, choose 2x or 4x, compare results, and see the cost first.';

export const VIDEO_UPSCALE_DATE_PUBLISHED = '2026-07-22';
export const VIDEO_UPSCALE_LAST_UPDATED = '2026-07-24';
export const VIDEO_UPSCALE_LAST_UPDATED_LABEL = 'July 24, 2026';

export const VIDEO_UPSCALE_KEYWORDS = [
  'ai video upscaler',
  'ai video upscaler to 4k',
  'upscale video to 4k',
  '4k video upscaler',
  'best ai video upscaler',
  '#1 ai video upscaler',
  'video enhancer',
  'video quality enhancer',
  'online video upscaler',
  'video upscaler 4k',
  'upscale video to 1080p',
  'video upscaling software',
  'best video upscaler',
  'enhance low-resolution video',
  'make blurry video clearer',
  'fix compressed video',
  'old video restoration',
  'ai-generated video enhancement',
  'video super-resolution',
].join(', ');

export const VIDEO_UPSCALE_QUICK_ANSWER =
  'Grok Imagine is an AI video upscaler and video quality enhancer for short MP4 or MOV clips. Upload a video up to 10MB and 30 seconds, choose 2x or 4x, preview the output and credit cost, then compare before and after. A 960 x 540 input can upscale video to 4K at 3840 x 2160; smaller inputs can upscale video to 1080p or another calculated size.';

export const VIDEO_UPSCALE_FEATURES = [
  {
    title: 'Upscale video to 1080p or 4K',
    body: 'Turn SD, 720p, or 1080p footage into a larger output that is easier to review and deliver. A 960 x 540 input reaches 3840 x 2160 with 4x processing; other input sizes produce different dimensions.',
    outcome:
      'Start with the highest-quality original that meets the 10MB and 30-second limits, then check the estimated output size before processing.',
    keyword: 'upscale video to 4K',
  },
  {
    title: 'Use a video enhancer for blurry or compressed footage',
    body: 'A video quality enhancer creates a larger working frame for footage with soft edges, codec blocks, banding, or repeated social exports. It can improve perceived detail, but severe blur and missing information still need manual review or a dedicated restoration step.',
    outcome:
      'Inspect text, faces, product edges, and high-contrast lines instead of judging the result from one paused frame.',
    keyword: 'make blurry video clearer',
  },
  {
    title: 'Prepare old, noisy, or low-frame-rate footage',
    body: 'Use AI video upscaling as one step in a larger workflow for VHS transfers, old home videos, noisy phone clips, shaky footage, or low-frame-rate recordings. Denoising, stabilization, deinterlacing, color restoration, and frame interpolation may still require a separate tool.',
    outcome:
      'Upscale after choosing the cleanest available copy and before final delivery or a second editing pass.',
    keyword: 'old video restoration',
  },
  {
    title: 'Polish AI-generated videos before publishing',
    body: 'Send a completed Grok image-to-video or text-to-video result into the AI video upscaler without rebuilding its prompt or motion. Use the larger result to inspect faces, texture, text, and moving edges before publishing.',
    outcome:
      'Keep the original and enhanced output together in the same Video AI history workflow.',
    keyword: 'AI-generated video enhancement',
  },
  {
    title: 'Compare the video quality enhancer result',
    body: 'The workbench shows the original and upscaled video side by side after processing. Play the complete clip, compare motion and fine detail, then download only the version that passes your review.',
    outcome:
      'Review before delivery instead of trusting a single preview frame or a resolution number.',
    keyword: 'video quality enhancer',
  },
] as const;

export const VIDEO_UPSCALE_WHY_CHOOSE = [
  {
    title: 'See whether the output reaches 4K before processing',
    body: 'The 4K video upscaler calculates output width and height from the uploaded video and selected factor. A 960 x 540 input shows 3840 x 2160 at 4x before processing.',
    proof:
      'Verify the input size, selected factor, estimated output, and credit estimate in the workbench.',
  },
  {
    title: 'Upscale saved Video AI results',
    body: 'This browser-based video upscaling software can open a completed Grok image-to-video or text-to-video result without rebuilding its prompt or motion.',
    proof:
      'The saved-video handoff loads the selected result into the Video AI workbench.',
  },
  {
    title: 'Inspect real before-and-after examples',
    body: 'Four public comparison players let you inspect color, texture, edges, compression, and motion at the same timeline position.',
    proof:
      'Drag the divider or play the synchronized clips in the before-and-after section.',
  },
  {
    title: 'See the cost before processing',
    body: 'The workbench calculates credits from the rounded-up duration and selected factor before the task starts.',
    proof:
      'The estimate uses 40 credits per second for 2x, 70 for 4x, and a 100-credit minimum.',
  },
  {
    title: 'Keep the original clip reviewable throughout the workflow',
    body: 'Upload in the browser, restore the draft after login, and review the completed result before downloading it.',
    proof:
      'The draft preserves the selected factor, uploaded video reference, and pending result.',
  },
] as const;

export const VIDEO_UPSCALE_PROOF_CASES = [
  {
    title: 'Restore old handheld footage',
    description:
      'A degraded home-video style recording is restored for cleaner contrast, color, and subject detail.',
    source: '1920 x 1080 degraded input',
    output: '1920 x 1080 enhanced export',
    duration: '10 seconds',
    durationIso: 'PT10S',
    uploadDate: '2026-07-23T00:00:00+00:00',
    focus: 'Skin tone, doorway edges, floor texture, noise, and color cast.',
    beforeVideo: '/video-upscale/old-home-video-restoration-before.mp4',
    beforePoster: '/video-upscale/old-home-video-restoration-before-poster.jpg',
    afterVideo: '/video-upscale/old-home-video-restoration-after.mp4',
    afterPoster: '/video-upscale/old-home-video-restoration-after-poster.jpg',
    beforeAlt: 'Degraded handheld home video before AI video restoration',
    afterAlt: 'Handheld home video after AI restoration and enhancement',
  },
  {
    title: 'Clean up a talking-head social clip',
    description:
      'A soft presenter video is enhanced for clearer facial detail and a cleaner background before social publishing.',
    source: '1920 x 1080 compressed input',
    output: '1920 x 1080 enhanced export',
    duration: '10 seconds',
    durationIso: 'PT10S',
    uploadDate: '2026-07-23T00:00:00+00:00',
    focus:
      'Eyes, hair, shirt texture, background edges, and compression noise.',
    beforeVideo: '/video-upscale/talking-head-social-video-before.mp4',
    beforePoster: '/video-upscale/talking-head-social-video-before-poster.jpg',
    afterVideo: '/video-upscale/talking-head-social-video-after.mp4',
    afterPoster: '/video-upscale/talking-head-social-video-after-poster.jpg',
    beforeAlt: 'Soft talking-head social video before AI enhancement',
    afterAlt: 'Talking-head social video after AI detail enhancement',
  },
  {
    title: 'Polish an AI-generated cinematic scene',
    description:
      'A stylized landscape clip is refined for cleaner silhouettes, surface texture, and more stable cinematic detail.',
    source: '1920 x 1080 AI video input',
    output: '1920 x 1080 polished export',
    duration: '15 seconds',
    durationIso: 'PT15S',
    uploadDate: '2026-07-23T00:00:00+00:00',
    focus:
      'Cliff edges, water reflections, figure silhouette, and fine texture.',
    beforeVideo: '/video-upscale/ai-generated-video-polish-before.mp4',
    beforePoster: '/video-upscale/ai-generated-video-polish-before-poster.jpg',
    afterVideo: '/video-upscale/ai-generated-video-polish-after.mp4',
    afterPoster: '/video-upscale/ai-generated-video-polish-after-poster.jpg',
    beforeAlt: 'AI-generated cinematic landscape before video polishing',
    afterAlt: 'AI-generated cinematic landscape after video enhancement',
  },
  {
    title: 'Improve gameplay UI and motion detail',
    description:
      'A busy gameplay recording is enhanced so interface text, character edges, and fast-moving scene detail are easier to inspect.',
    source: '1920 x 1080 gameplay input',
    output: '1920 x 1080 enhanced export',
    duration: '10 seconds',
    durationIso: 'PT10S',
    uploadDate: '2026-07-23T00:00:00+00:00',
    focus:
      'HUD text, ability icons, character outlines, neon signs, and motion.',
    beforeVideo: '/video-upscale/gameplay-video-upscale-before.mp4',
    beforePoster: '/video-upscale/gameplay-video-upscale-before-poster.jpg',
    afterVideo: '/video-upscale/gameplay-video-upscale-after.mp4',
    afterPoster: '/video-upscale/gameplay-video-upscale-after-poster.jpg',
    beforeAlt: 'Gameplay recording before AI video enhancement',
    afterAlt: 'Gameplay recording after AI video detail enhancement',
  },
] as const;

export const VIDEO_UPSCALE_STEPS = [
  {
    name: 'Upload the cleanest video you have',
    text: 'Choose an MP4 or MOV up to 10MB and 30 seconds. Use the highest-quality original available because an AI video upscaler cannot recreate detail that was never captured.',
  },
  {
    name: 'Check the 4K output estimate',
    text: 'Choose 2x for a moderate increase or 4x for a larger output. When the input dimensions support it, the workbench shows a 3840 x 2160 4K estimate before you spend credits.',
  },
  {
    name: 'Run the AI video upscale',
    text: 'Start the task after checking the output and credit estimate. The task continues after submission, and returning to this page restores the pending job in the result panel.',
  },
  {
    name: 'Compare, then download',
    text: 'Play the original and upscaled video at the same size. Check faces, text, thin lines, motion, noise, and compression artifacts before downloading the result.',
  },
] as const;

export const VIDEO_UPSCALE_USE_CASES = [
  {
    title: 'Creators preparing social clips for larger screens',
    input: 'A short 720p or 1080p clip exported from an older project.',
    action:
      'Upload the cleanest copy, check whether 2x or 4x reaches the target frame, and inspect motion and edge detail.',
    result:
      'A larger video draft that is easier to reuse in a current vertical, landscape, or 4K edit.',
    intent: 'upscale video to 4K for social media',
  },
  {
    title: 'Marketers repairing compressed product footage',
    input:
      'A product clip received through chat, a shared drive, or a social export.',
    action:
      'Use the AI video upscaler at the smallest useful factor, then compare labels, product edges, and background texture.',
    result:
      'A larger file for an ad timeline, landing-page demo, or presentation review. Compression artifacts still need inspection.',
    intent: 'fix compressed video quality online',
  },
  {
    title: 'AI video creators refining generated drafts',
    input: 'A completed Grok image-to-video or text-to-video generation.',
    action:
      'Open Upscale from the result toolbar and process the saved video without rebuilding the prompt or motion.',
    result:
      'A larger version that keeps the original motion while giving you a cleaner frame for final review.',
    intent: 'AI-generated video enhancement',
  },
] as const;

export const VIDEO_UPSCALE_VALUE_ROWS = [
  {
    factor: '2x upscale',
    dimensions: 'Input width x2, input height x2',
    creditFormula: 'max(100, ceil(seconds) x 40)',
    bestFor: 'Routine enlargement, social clips, and a moderate quality lift.',
    tradeoff:
      'Uses fewer credits per second and is usually the safer first test.',
  },
  {
    factor: '4x upscale',
    dimensions: 'Input width x4, input height x4',
    creditFormula: 'max(100, ceil(seconds) x 70)',
    bestFor:
      'Small input frames that need a substantially larger output or a 4K delivery frame.',
    tradeoff:
      'Uses more credits and can make defects in the original footage more visible during review.',
  },
  {
    factor: 'Native high-resolution input',
    dimensions: 'No dimension change',
    creditFormula: 'Not an upscale job',
    bestFor:
      'Footage that is already sharp and exported at the target dimensions.',
    tradeoff:
      'May not benefit enough from upscaling to justify another processing pass.',
  },
] as const;

export const VIDEO_UPSCALE_CALCULATION_EXAMPLES = [
  {
    source: '960 x 540',
    factor: '2x',
    output: '1920 x 1080',
    duration: '5 seconds',
    credits: '200 credits',
  },
  {
    source: '960 x 540',
    factor: '4x',
    output: '3840 x 2160',
    duration: '5 seconds',
    credits: '350 credits',
  },
  {
    source: '960 x 540',
    factor: '4x',
    output: '3840 x 2160',
    duration: '30 seconds',
    credits: '2,100 credits',
  },
] as const;

export const VIDEO_UPSCALE_LIMITS = [
  {
    title: '4x does not always mean 4K',
    body: 'The output dimensions are calculated from the input dimensions. A 960 x 540 video can reach 3840 x 2160 with 4x processing, while a smaller input produces a different result.',
  },
  {
    title: 'Upscaling is not every repair mode',
    body: 'AI video upscaling can enlarge frames and improve perceived detail. Denoising, stabilization, frame interpolation, deinterlacing, color restoration, and face enhancement may still require another processing step.',
  },
  {
    title: 'Missing detail cannot be guaranteed',
    body: 'AI upscaling can reconstruct plausible texture and sharper edges, but it cannot guarantee correct tiny text, faces, logos, or product labels when the original clip is heavily blurred.',
  },
  {
    title: 'Short clips are the current boundary',
    body: 'Direct uploads currently support MP4 and MOV files up to 10MB and 30 seconds. Split longer videos into short scenes, upscale each scene, and review continuity before editing them together.',
  },
  {
    title: 'Review motion, not only a paused frame',
    body: 'A still frame can look sharp while motion reveals flicker, halos, or unstable fine detail. Play the entire result before publishing or using it in paid creative.',
  },
] as const;

export const VIDEO_UPSCALE_FAQS = [
  {
    question: 'Can I upscale video to 4K online?',
    answer:
      'Yes, when the input dimensions and selected factor reach a 3840 x 2160 frame. For example, 960 x 540 multiplied by four becomes 3840 x 2160. Smaller inputs can still improve, but the final output may remain below 4K.',
  },
  {
    question:
      'Can a video enhancer or AI video upscaler fix blurry, noisy, or compressed video?',
    answer:
      'It can improve perceived size, edges, and texture in soft or compressed footage. Severe blur, missing focus, heavy noise, and codec damage may need dedicated sharpening or restoration steps, and the complete clip should be reviewed before delivery.',
  },
  {
    question: 'Can a 4K video upscaler restore VHS or old videos?',
    answer:
      'Upscaling can be one step in an old-video restoration workflow after capture and basic cleanup. VHS and archival footage may still need denoising, deinterlacing, flicker repair, color restoration, or colorization before the final 4K upscale.',
  },
  {
    question:
      'Can I increase FPS, stabilize, or enhance faces with video upscale?',
    answer:
      'AI video upscaling increases frame dimensions; it does not replace frame interpolation, stabilization, or a dedicated face-enhancement pass. Use the upscaler as one part of the workflow, then check motion, facial identity, text, and edges in the final result.',
  },
  {
    question: 'How are AI video upscale credits calculated?',
    answer:
      'Grok Imagine calculates video upscale credits from the rounded-up clip duration and selected factor. A 2x upscale costs 40 credits per second and a 4x upscale costs 70 credits per second, with a minimum charge of 100 credits. The exact estimate appears before processing.',
  },
] as const;

export const VIDEO_UPSCALE_SCHEMA_FEATURES = [
  'AI video upscaling toward 4K when input dimensions support it, at 2x or 4x',
  'Output dimensions calculated from input width, height, and factor',
  'MP4 and MOV video uploads',
  'Direct uploads up to 10MB and 30 seconds',
  'Workflow guidance for blurry, noisy, compressed, old, and AI-generated video',
  'Original and output video comparison',
  'Credit estimate based on rounded duration and upscale factor',
  'Private saved output and Video AI history integration',
] as const;

export function getVideoUpscaleSeoData() {
  return {
    path: VIDEO_UPSCALE_PATH,
    seoTitle: VIDEO_UPSCALE_SEO_TITLE,
    seoDescription: VIDEO_UPSCALE_SEO_DESCRIPTION,
    keywords: VIDEO_UPSCALE_KEYWORDS,
    datePublished: VIDEO_UPSCALE_DATE_PUBLISHED,
    lastUpdated: VIDEO_UPSCALE_LAST_UPDATED,
    lastUpdatedLabel: VIDEO_UPSCALE_LAST_UPDATED_LABEL,
    quickAnswer: VIDEO_UPSCALE_QUICK_ANSWER,
    features: VIDEO_UPSCALE_FEATURES,
    whyChoose: VIDEO_UPSCALE_WHY_CHOOSE,
    proofCases: VIDEO_UPSCALE_PROOF_CASES,
    steps: VIDEO_UPSCALE_STEPS,
    useCases: VIDEO_UPSCALE_USE_CASES,
    valueRows: VIDEO_UPSCALE_VALUE_ROWS,
    limits: VIDEO_UPSCALE_LIMITS,
    faqs: VIDEO_UPSCALE_FAQS,
    calculationExamples: VIDEO_UPSCALE_CALCULATION_EXAMPLES,
    schemaFeatures: VIDEO_UPSCALE_SCHEMA_FEATURES,
  };
}
